import React, { useState, useEffect } from 'react';
import { Typography, Row, Col, Button, Input, Select, Card, Space, Tag, Rate, Image } from 'antd';
import { SearchOutlined, StarOutlined, ClockCircleOutlined, HeartOutlined } from '@ant-design/icons';
import FeaturedSection from '../../UI/FeaturedSection';
import MarketplaceGrid from '../../UI/MarketplaceGrid';
import MarketplaceMenuCard from '../../UI/MarketplaceMenuCard';
import MarketplaceVendorCard from '../../UI/MarketplaceVendorCard';
import { getImageForCard } from '../../../utils/images';
import api from '../../../services/api';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const MarketplaceHome = () => {
  const [featuredMenus, setFeaturedMenus] = useState([]);
  const [featuredVendors, setFeaturedVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState(new Set());

  useEffect(() => {
    loadHomeData();
  }, []);

  const loadHomeData = async () => {
    try {
      setLoading(true);
      
      const [menusRes, vendorsRes, categoriesRes] = await Promise.all([
        api.get('/menus', { params: { featured: true, limit: 8 } }),
        api.get('/vendors', { params: { featured: true, limit: 6 } }),
        api.get('/categories', { params: { active: true } })
      ]);

      if (menusRes.data.success) {
        setFeaturedMenus(menusRes.data.data.data || menusRes.data.data);
      }

      if (vendorsRes.data.success) {
        setFeaturedVendors(vendorsRes.data.data.data || vendorsRes.data.data);
      }

      if (categoriesRes.data.success) {
        setCategories(categoriesRes.data.data);
      }
    } catch (error) {
      console.error('Error loading home data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (menu) => {
    // Implement add to cart logic
    console.log('Add to cart:', menu);
  };

  const handleToggleFavorite = (item) => {
    const newFavorites = new Set(favorites);
    if (favorites.has(item.id)) {
      newFavorites.delete(item.id);
    } else {
      newFavorites.add(item.id);
    }
    setFavorites(newFavorites);
  };

  const handleViewDetails = (item) => {
    // Navigate to detail page
    console.log('View details:', item);
  };

  const renderMenuCard = (menu) => (
    <MarketplaceMenuCard
      menu={menu}
      onAddToCart={handleAddToCart}
      onViewDetails={handleViewDetails}
      onToggleFavorite={handleToggleFavorite}
      isFavorite={favorites.has(menu.id)}
      showAddToCart={true}
      showFavorite={true}
      showVendor={true}
      imageHeight={200}
    />
  );

  const renderVendorCard = (vendor) => (
    <MarketplaceVendorCard
      vendor={vendor}
      onViewDetails={handleViewDetails}
      onToggleFavorite={handleToggleFavorite}
      isFavorite={favorites.has(vendor.id)}
      showFavorite={true}
      showStats={true}
      imageHeight={180}
    />
  );

  return (
    <div style={{ minHeight: '100vh', background: '#fafafa' }}>
      {/* Hero Section */}
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '80px 0',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <Title level={1} style={{ color: 'white', fontSize: '48px', marginBottom: '16px' }}>
            Discover Amazing Food
          </Title>
          <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '20px', marginBottom: '32px', display: 'block' }}>
            Order from your favorite restaurants and get it delivered fast
          </Text>
          
          {/* Search Bar */}
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <Search
              placeholder="Search for food, restaurants, or cuisines..."
              enterButton={<SearchOutlined />}
              size="large"
              style={{ 
                borderRadius: '50px',
                boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
              }}
            />
          </div>
        </div>
      </div>

      {/* Categories Section */}
      <div style={{ padding: '48px 24px', maxWidth: '1200px', margin: '0 auto' }}>
        <Title level={2} style={{ textAlign: 'center', marginBottom: '32px' }}>
          Browse by Category
        </Title>
        <Row gutter={[16, 16]}>
          {categories.slice(0, 8).map((category) => (
            <Col xs={12} sm={8} md={6} lg={3} key={category.id}>
              <Card
                hoverable
                style={{
                  textAlign: 'center',
                  borderRadius: '12px',
                  height: '120px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'white',
                  border: '1px solid #f0f0f0'
                }}
              >
                <div>
                  <Image
                    src={getImageForCard(category)}
                    alt={category.name}
                    style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '8px' }}
                    fallback="/images/placeholder.svg"
                  />
                  <div style={{ marginTop: '8px', fontSize: '14px', fontWeight: '500' }}>
                    {category.name}
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* Featured Menus */}
      <div style={{ padding: '0 24px 48px', maxWidth: '1200px', margin: '0 auto' }}>
        <FeaturedSection
          title="Featured Dishes"
          subtitle="Our most popular and delicious menu items"
          items={featuredMenus}
          type="menu"
          loading={loading}
          onViewAll={() => window.location.href = '/menu-listing'}
        />
      </div>

      {/* Featured Vendors */}
      <div style={{ padding: '0 24px 48px', maxWidth: '1200px', margin: '0 auto' }}>
        <FeaturedSection
          title="Top Restaurants"
          subtitle="Highly rated restaurants near you"
          items={featuredVendors}
          type="vendor"
          loading={loading}
          onViewAll={() => window.location.href = '/vendor-listing'}
        />
      </div>

      {/* Quick Stats */}
      <div style={{ 
        background: 'white', 
        padding: '48px 24px',
        borderTop: '1px solid #f0f0f0'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <Row gutter={[32, 32]} justify="center">
            <Col xs={12} sm={8} md={6} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#1890ff', marginBottom: '8px' }}>
                1000+
              </div>
              <Text type="secondary">Restaurants</Text>
            </Col>
            <Col xs={12} sm={8} md={6} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#52c41a', marginBottom: '8px' }}>
                5000+
              </div>
              <Text type="secondary">Menu Items</Text>
            </Col>
            <Col xs={12} sm={8} md={6} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#fa8c16', marginBottom: '8px' }}>
                30min
              </div>
              <Text type="secondary">Avg Delivery</Text>
            </Col>
            <Col xs={12} sm={8} md={6} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#eb2f96', marginBottom: '8px' }}>
                4.8★
              </div>
              <Text type="secondary">Avg Rating</Text>
            </Col>
          </Row>
        </div>
      </div>

      {/* CTA Section */}
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '60px 24px',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <Title level={2} style={{ color: 'white', marginBottom: '16px' }}>
            Ready to Order?
          </Title>
          <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '18px', marginBottom: '32px', display: 'block' }}>
            Join thousands of satisfied customers and start ordering today
          </Text>
          <Space size="large">
            <Button 
              type="primary" 
              size="large"
              style={{ 
                background: 'white',
                color: '#667eea',
                border: 'none',
                borderRadius: '8px',
                height: '48px',
                padding: '0 32px',
                fontWeight: 'bold'
              }}
            >
              Start Ordering
            </Button>
            <Button 
              size="large"
              style={{ 
                color: 'white',
                border: '2px solid white',
                borderRadius: '8px',
                height: '48px',
                padding: '0 32px',
                fontWeight: 'bold'
              }}
            >
              Learn More
            </Button>
          </Space>
        </div>
      </div>
    </div>
  );
};

export default MarketplaceHome;
